import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import notification from "antd/es/notification";
import { useIntl } from "react-intl";

import Folder from "@mapmycustomers/shared/types/entity/Folder";
import Resource from "@mapmycustomers/shared/types/entity/Resource";
import { Modal, SelectField } from "@mapmycustomers/ui";

import { getUserFolders } from "@app/scene/resources/store";
import { updateResource } from "@app/scene/resources/store/actions";
import { RootState } from "@app/store/rootReducer";

import styles from "./ActionsButton.module.scss";
import messages from "./messages";

export interface MoveToFolderModalProps {
  folders: Folder[];
  onHide: () => void;
  onUpdateResource: typeof updateResource.request;
  open: boolean;
  resource: Resource;
}

const MoveToFolderModal: FC<MoveToFolderModalProps> = ({
  folders,
  onHide,
  onUpdateResource,
  open,
  resource,
}) => {
  const intl = useIntl();

  const [newFolderId, setNewFolderId] = useState<Folder["id"] | undefined>(resource.folder?.id);

  const handleMoveToFolder = useCallback(() => {
    if (newFolderId) {
      onUpdateResource({
        resource: {
          id: resource.id,
          folder: { id: newFolderId },
        },
        successCallback: () => {
          const folderName = folders.find(({ id }) => id === newFolderId)?.name;
          notification.success({
            message: intl.formatMessage(messages.moveToFolderSuccess, {
              folderName,
              resourceName: resource.name,
            }),
          });
          onHide();
        },
      });
    }
  }, [folders, intl, newFolderId, onHide, onUpdateResource, resource.id, resource.name]);

  const folderOptions = useMemo(
    () => folders.map(({ id, name }) => ({ label: name, value: id })),
    [folders]
  );

  const handleCancel = useCallback(() => {
    setNewFolderId(resource.folder?.id);
    onHide();
  }, [onHide, resource.folder?.id]);

  useEffect(() => {
    setNewFolderId(resource.folder?.id);
  }, [resource.folder?.id]);

  return (
    <Modal
      className={styles.moveToFolderModal}
      destroyOnClose
      okButtonProps={{ disabled: newFolderId === resource.folder?.id }}
      okText={intl.formatMessage(messages.apply)}
      onCancel={handleCancel}
      onOk={handleMoveToFolder}
      open={open}
      title={intl.formatMessage(messages.moveToFolderTitle, {
        name: <span className={styles.name}>{resource.name}</span>,
      })}
    >
      <SelectField<Folder["id"]>
        label={intl.formatMessage(messages.moveToFolderLabel)}
        onChange={setNewFolderId}
        options={folderOptions}
        value={newFolderId}
      />
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  folders: getUserFolders(state),
});

const mapDispatchToProps = {
  onUpdateResource: updateResource.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(MoveToFolderModal);
